import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { GetLanguageString } from "../helper/Components";
import Draggable from "react-draggable";
import { LoadingSpinner, applyClassToCharacters } from "../../constants/const";
import { ApiFinance } from "../funds/ApiFinance";

//#region styles (Pattern matched to reference)
const cardStyle = (borderColor) => ({
  borderRadius: "12px",
  border: "1px solid #E5E7EB",
  borderLeft: `4px solid ${borderColor}`,
  background: "#FFFFFF",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  marginBottom: "16px",
});

const labelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#9CA3AF",
  marginBottom: "4px",
};

const valueStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#1F2937",
  lineHeight: "1.4",
};

const sectionHeaderStyle = (color) => ({
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  color: color,
  textTransform: "uppercase",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const animatedBody = {
  animation: "fadeSlideIn 0.25s ease-out",
  padding: "20px 24px",
};

const amountBadgeStyle = {
  display: "inline-block",
  padding: "4px 12px",
  borderRadius: "30px",
  backgroundColor: "#EFF6FF",
  color: "#2563EB",
  fontWeight: 700,
  fontSize: "14px",
  border: "1px solid #BFDBFE",
};
//#endregion

const FundsOtherIncomeNotificationDetails = ({
  setViewOtherIncomeNotificationDetails,
  notificationId,
}) => {
  const [notificationresponse, setNotificationresponse] = useState({});
  const [loading, setLoading] = useState(false);
  const toggle = () => setViewOtherIncomeNotificationDetails(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const handleDrag = (e, ui) =>
    setPosition((p) => ({ x: p.x + ui.deltaX, y: p.y + ui.deltaY }));

  useEffect(() => {
    if (!notificationId) return;
    setLoading(true);
    ApiFinance()
      .GetOtherIncomeDataForNotification(notificationId)
      .then((res) => {
        if (res.data) setNotificationresponse(res.data || {});
      })
      .finally(() => setLoading(false));
  }, [notificationId]);

  return (
    <>
      <style>
        {`
          @keyframes fadeSlideIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .inmodal .modal-content {
            border-radius: 16px !important;
            border: none !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            overflow: hidden;
          }
          .inmodal .modal-header {
            background-color: #F9FAFB !important;
            border-bottom: 1px solid #F3F4F6 !important;
            padding: 16px 24px !important;
          }
          .inmodal .modal-footer {
            background-color: #F9FAFB !important;
            border-top: 1px solid #F3F4F6 !important;
            padding: 16px 24px !important;
          }
          .inmodal .modal-title {
            font-weight: 700 !important;
            font-size: 16px !important;
            color: #111827 !important;
            display: flex;
            align-items: center;
            gap: 8px;
          }
        `}
      </style>

      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal size="lg" isOpen className="inmodal">
          <ModalHeader className="modal-title">
            <i className="fa fa-info-circle text-primary"></i>
            <span>
              <GetLanguageString props="courses_report_details" />
            </span>
          </ModalHeader>

          {loading ? (
            <ModalBody className="text-center py-4">
              <LoadingSpinner />
            </ModalBody>
          ) : (
            <ModalBody style={animatedBody}>
              <Card style={cardStyle("#3B82F6")}>
                <CardBody style={{ padding: "20px 24px" }}>
                  <div style={sectionHeaderStyle("#2563EB")}>
                    <i className="fa fa-info-circle" style={{ fontSize: "15px" }}></i>
                    {<GetLanguageString props="courses_report_details" />}
                  </div>
                  <Row className="gy-3">
                    <Col md="6">
                      <div style={labelStyle}>
                        {<GetLanguageString props="transactions_memo" />}
                      </div>
                      <div style={valueStyle}>{notificationresponse.memo}</div>
                    </Col>
                    <Col md="6">
                      <div style={labelStyle}>
                        {<GetLanguageString props="funds_receipt_number" />}
                      </div>
                      <div style={valueStyle}>
                        {notificationresponse.recieptNumber || notificationresponse.reference}
                      </div>
                    </Col>
                    <Col md="6">
                      <div style={labelStyle}>
                        {<GetLanguageString props="funds_add_fund_amount" />}
                      </div>
                      <div>
                        <div style={amountBadgeStyle}>
                          {new Intl.NumberFormat().format(
                            notificationresponse.amount ||
                            notificationresponse.transferAmount ||
                            0,
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div style={labelStyle}>
                        {<GetLanguageString props="funds_date" />}
                      </div>
                      <div style={valueStyle}>
                        {notificationresponse.date ||
                          notificationresponse.requestDate
                          ? new Date(
                            notificationresponse.date ||
                            notificationresponse.requestDate,
                          ).toLocaleDateString("en-GB")
                          : ""}
                      </div>
                    </Col>
                    {notificationresponse.accountHead && (
                      <Col md="6">
                        <div style={labelStyle}>
                          {<GetLanguageString props="funds_account" />}
                        </div>
                        <div style={valueStyle}>{notificationresponse.accountHead}</div>
                      </Col>
                    )}
                    {notificationresponse.dimension2Name && (
                      <Col md="6">
                        <div style={labelStyle}>
                          {<GetLanguageString props="funds_dimension_2" />}
                        </div>
                        <div style={valueStyle}>{notificationresponse.dimension2Name}</div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>

              {/* INCOME DETAILS TABLE FOR MULTIPLE CATEGORIES */}
              {notificationresponse.entries &&
                (notificationresponse.notificationCode === "req_expense_from_amanat"
                  ? notificationresponse.entries.length >= 0
                  : notificationresponse.entries.length > 0) && (
                  <Card style={cardStyle("#10B981")}>
                    <CardBody style={{ padding: "20px 24px" }}>
                      <div style={sectionHeaderStyle("#059669")}>
                        <i className="fa fa-list" style={{ fontSize: "15px" }}></i>
                        <GetLanguageString
                          props={
                            (notificationresponse.notificationCode === "req_expense_from_amanat" ||
                             notificationresponse.notificationCode === "req_halqa_share" ||
                             notificationresponse.notificationCode === "req_markaz_share")
                              ? "finance_other_expense_entry_details"
                              : "finance_other_income_entry_details"
                          }
                        />
                      </div>
                      <div className="table-responsive no-drag" style={{ borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px", borderCollapse: "separate" }}>
                          <thead>
                            <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
                              <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>#</th>
                              <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="fund_account_head" />}</th>
                              <th style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="transactions_memo" />}</th>
                              <th className="text-end" style={{ padding: "10px 16px", fontWeight: 600, color: "#4B5563" }}>{<GetLanguageString props="funds_add_fund_amount" />}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notificationresponse.entries.map((entry, index) => (
                              <tr key={index} style={{ borderBottom: "1px solid #F3F4F6" }}>
                                <td style={{ padding: "10px 16px", color: "#6B7280", fontWeight: 500 }}>{index + 1}</td>
                                <td style={{ padding: "10px 16px", color: "#1F2937", fontWeight: 600 }}>
                                  {entry.accountHead}
                                  {entry.accountHead &&
                                    !entry.accountHead.toLowerCase().includes("loan") &&
                                    !entry.accountHead.toLowerCase().includes("maktaba") &&
                                    (notificationresponse.rafiqBankName || notificationresponse.rafiqAccountNumber) && (
                                      <div style={{ fontSize: "11px", color: "#7C3AED", fontWeight: "normal", marginTop: "2px" }}>
                                        <i className="fa fa-university" style={{ marginRight: "4px" }}></i>
                                        {notificationresponse.rafiqBankName} - {notificationresponse.rafiqAccountNumber} ({notificationresponse.rafiqAccountTitle})
                                      </div>
                                    )}
                                </td>
                                <td style={{ padding: "10px 16px", color: "#4B5563" }}>{entry.lineMemo || "-"}</td>
                                <td className="text-end" style={{ padding: "10px 16px", color: "#111827", fontWeight: 700, fontSize: "14px" }}>
                                  {new Intl.NumberFormat().format(entry.amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}

              {notificationresponse.notificationCode !== "req_expense_from_amanat" &&
              (notificationresponse.rafiqAccountTitle || notificationresponse.rafiqAccountNumber || notificationresponse.rafiqBankName) ? (
                <Card style={cardStyle("#8B5CF6")}>
                  <CardBody style={{ padding: "20px 24px" }}>
                    <div style={sectionHeaderStyle("#7C3AED")}>
                      <i className="fa fa-university" style={{ fontSize: "15px" }}></i>
                      <GetLanguageString props="transactions_beneficiary_details" />
                    </div>

                    <Row className="gy-3">
                      {/* Subheading: Vendor Info */}
                      {(notificationresponse.vendorName || notificationresponse.cnic) && (
                        <Col md="12">
                          <h6 style={{ fontWeight: 600, color: "#4B5563", fontSize: "12px", borderBottom: "1px dashed #D1D5DB", paddingBottom: "4px", marginBottom: "8px" }}>
                            Vendor Details / وینڈر کی تفصیلات
                          </h6>
                        </Col>
                      )}

                      {notificationresponse.vendorName && (
                        <Col md="6">
                          <div style={labelStyle}>
                            <GetLanguageString props="transactions_vendor_name" />
                          </div>
                          <div style={valueStyle}>
                            {applyClassToCharacters(notificationresponse.vendorName, "ur")}
                          </div>
                        </Col>
                      )}

                      {notificationresponse.cnic && (
                        <Col md="6">
                          <div style={labelStyle}>
                            <GetLanguageString props="transactions_vendor_cnic" />
                          </div>
                          <div style={valueStyle}>
                            {applyClassToCharacters(notificationresponse.cnic, "ur")}
                          </div>
                        </Col>
                      )}

                      {/* Subheading: Bank Account */}
                      <Col md="12" style={{ marginTop: "16px" }}>
                        <h6 style={{ fontWeight: 600, color: "#4B5563", fontSize: "12px", borderBottom: "1px dashed #D1D5DB", paddingBottom: "4px", marginBottom: "8px" }}>
                          Bank Account Details / بینک اکاؤنٹ کی تفصیلات
                        </h6>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_title" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(notificationresponse.rafiqAccountTitle, "ur")}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_account_number" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(notificationresponse.rafiqAccountNumber, "ur")}
                        </div>
                      </Col>

                      <Col md="4">
                        <div style={labelStyle}>
                          <GetLanguageString props="transactions_bank_name" />
                        </div>
                        <div style={valueStyle}>
                          {applyClassToCharacters(notificationresponse.rafiqBankName, "ur")}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ) : null}
            </ModalBody>
          )}

          <ModalFooter>
            <Button
              color="primary"
              style={{ borderRadius: "8px", padding: "8px 24px", fontWeight: 600 }}
              onClick={toggle}
            >
              <GetLanguageString props="users_role_ok_btn" />
            </Button>
          </ModalFooter>
        </Modal>
      </Draggable>
    </>
  );
};

export default FundsOtherIncomeNotificationDetails;
